<template>
    <div class="p-4">
        <div class="mb-4 flex items-center gap-2 text-sm font-semibold text-theme-accent">
            <svg-icon icon="check-circle" />
            <span>{{ linkReadyLabel }}</span>
        </div>

        <div class="mb-4 flex gap-2">
            <input
                ref="urlInput"
                type="text"
                class="min-w-0 flex-1 rounded-md border border-theme-border bg-transparent px-2.5 py-2 text-xs text-theme-text"
                :value="url"
                readonly
                @focus="$event.target.select()"
            />
            <button
                type="button"
                class="flex-shrink-0 rounded-md bg-theme-buynow-bg px-3.5 py-2 text-xs font-semibold text-theme-buynow-text transition-opacity hover:opacity-85"
                @click="copyShareLink"
            >
                {{ isCopied ? '\u2713' : copyLinkLabel }}
            </button>
        </div>

        <div class="flex items-center gap-3">
            <span class="whitespace-nowrap text-xs text-theme-icon">{{ shareViaLabel }}</span>
            <div class="flex gap-2">
                <button
                    v-for="network in socialNetworks"
                    :key="network.id"
                    type="button"
                    class="flex h-9 w-9 items-center justify-center rounded-lg border border-theme-border bg-transparent transition-colors hover:bg-theme-icon-bg"
                    :aria-label="network.id"
                    @click="openSocialShare(network.id)"
                >
                    <svg-icon :icon="network.icon" width="20" height="20" :fill="network.fill" />
                </button>
            </div>
        </div>
    </div>
</template>

<script>
'use strict';

import { mapActions, mapMutations, mapState } from 'vuex';
import { uiAPI } from '../../../src/js/communication';

const SOCIAL_NETWORKS = [
    { id: 'whatsapp', icon: 'whatsapp-icon-24px', fill: '#25D366' },
    { id: 'x', icon: 'x-icon-24px', fill: null },
    { id: 'facebook', icon: 'facebook-icon-24px', fill: '#1877F2' },
];

const COPY_FEEDBACK_DURATION_MS = 2000;

export default {
    props: {
        url: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            isCopied: false,
            copyFeedbackTimerId: null,
            socialNetworks: SOCIAL_NETWORKS,
        };
    },
    computed: {
        ...mapState(['localizations', 'lang']),
        linkReadyLabel() {
            return this.localizations[this.lang].sharing?.linkReady || 'Link ready';
        },
        copyLinkLabel() {
            return this.localizations[this.lang].sharing?.copyLink || 'Copy link';
        },
        shareViaLabel() {
            return this.localizations[this.lang].sharing?.shareVia || 'Share via';
        },
    },
    beforeDestroy() {
        if (this.copyFeedbackTimerId) {
            clearTimeout(this.copyFeedbackTimerId);
        }
    },
    methods: {
        ...mapMutations(['openToast']),
        ...mapActions(['copyToClipBoard']),
        /**
         * Copies the share link to clipboard and shows feedback.
         * Skips the success UI if the copy fails (the action shows its own error toast).
         */
        async copyShareLink() {
            const wasCopied = await this.copyToClipBoard(this.url);
            if (!wasCopied) return;

            this.isCopied = true;
            this.openToast({ text: this.localizations[this.lang].sharing?.linkCopied || 'Link copied' });
            clearTimeout(this.copyFeedbackTimerId);
            this.copyFeedbackTimerId = setTimeout(() => {
                this.isCopied = false;
            }, COPY_FEEDBACK_DURATION_MS);
        },
        /**
         * Opens a social network share page via the host application.
         * @param {string} networkId - The social network identifier
         */
        openSocialShare(networkId) {
            const encodedUrl = encodeURIComponent(this.url);
            const shareUrls = {
                whatsapp: `https://api.whatsapp.com/send?text=${encodedUrl}`,
                x: `https://x.com/intent/tweet?url=${encodedUrl}`,
                facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
            };
            uiAPI.shareContent({ url: shareUrls[networkId] });
        },
    },
};
</script>
